<script setup lang="ts">
import type { BookAndArticle, Page } from '~/utils/solr/literature'
import { useBookUrl } from '~/composables/book/useBookUrl'
import { sortByProximity } from '~/utils/literature/search-result'
import { cleanHighlights } from '~/utils/solr/highlighting-utils'

const props = defineProps<{
  book: BookAndArticle | null
  searchMode: boolean
}>()

const emit = defineEmits<{
  showSearchResult: [Page, string[], userInitiated: boolean]
}>()

const searchForm = ref<HTMLFormElement | null>(null)
const searchInput = useTemplateRef('searchInput')

const KEY = 'q'
const route = useRoute<'book-by-id'>()

const searchTerm = useRouteQuery<string>(KEY, '')

const { searchResult, isLoading } = useLiteratureSearch(searchTerm, toRef(() => props.book?.id))

const { getCurrentPageId } = useBookUrl()

// Store the page ID when search results arrive so clicking results doesn't re-sort the list
const sortAnchorPageId = ref<string | undefined>()
watch(() => props.searchMode, (isSearchMode) => {
  if (isSearchMode) {
    sortAnchorPageId.value = getCurrentPageId()
  }
}, { immediate: true })

const proximityDocs = computed(() => {
  const docs = searchResult.value?.response.docs
  if (!docs) {
    return []
  }
  return sortByProximity(docs, sortAnchorPageId.value)
})

function handleSearch(event: Event) {
  const data = new FormData(event.target as HTMLFormElement)
  const queryValue = data.get(KEY) as string

  if (!queryValue) {
    return resetSearch()
  }

  searchTerm.value = queryValue
}

function resetSearch() {
  searchTerm.value = ''
}

const handleSearchAsYouType = useDebounceFn((value: string) => {
  const isPageNumber = /^\d+$/.test(value.trim())
  const isSideNotation = /^s\.?\s*\d+$/i.test(value.trim())

  if (value.length > 2 || isPageNumber || isSideNotation) {
    submitSearch()
  }
}, 300)

function submitSearch() {
  if (searchForm.value) {
    searchForm.value.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
  }
}

function handleShowSearchResult(doc: Page, highlights: string[], userInitiated: boolean) {
  emit('showSearchResult', doc, highlights, userInitiated)
}

watch(() => route.query[KEY], (newValue) => {
  const newTerm = (newValue as string) || ''
  if (newTerm !== searchTerm.value) {
    searchTerm.value = newTerm
  }
})

watch(searchInput, (newValue) => {
  if (newValue) {
    newValue.focus()
  }
})

onMounted(() => {
  if (route.query[KEY] && props.book?.id) {
    submitSearch()
  }
})

watch(searchResult, (newValue) => {
  const docs = newValue?.response.docs
  const highlighting = newValue?.highlighting
  if (docs?.length === 1) {
    const doc = docs[0] ?? null
    if (!doc?.pageNumberString) {
      return
    }
    const highlights = highlighting?.[doc.id]?.bodytext || []
    handleShowSearchResult(doc, cleanHighlights(highlights), false)
  }
})
</script>

<template>
  <div v-show="searchMode" class="sticky top-12 max-md:top-[52px] z-[9] pb-m pt-xs bg-surface-elevated flex flex-col gap-m">
    <form ref="searchForm" class="bg-surface-elevated hidden md:flex flex-col gap-2xs" @submit.prevent="handleSearch($event)">
      <label for="literature" class="text-small font-normal">Søk i denne boka</label>
      <FormSearchInput
        id="literature"
        ref="searchInput"
        v-model="searchTerm"
        shape="square"
        external-label
        size="medium"
        name="q"
        resettable
        color="carbon"
        @update:model-value="handleSearchAsYouType"
        @reset="resetSearch"
      />
    </form>
    <div class="flex items-center gap-1 w-full">
      <Skeleton v-if="isLoading" class="h-4 w-3/5" />
      <span v-else class="text-caption flex-1">
        Viser {{ searchResult?.response.numFound }} treff i denne boken
      </span>
    </div>
  </div>
  <div class="flex flex-col gap-xs">
    <span class="sr-only">Søkeresultat</span>
    <template v-if="isLoading">
      <div v-for="n in 6" :key="n" class="px-s pt-xs pb-s w-full">
        <div class="flex flex-col gap-2xs w-full">
          <Skeleton class="h-5 rounded w-1/4" />
          <div class="flex flex-col gap-3xs">
            <Skeleton class="h-2 rounded w-full" />
            <Skeleton class="h-2 rounded w-2/3" />
            <Skeleton class="h-2 rounded w-2/5" />
          </div>
        </div>
      </div>
    </template>
    <template v-else-if="book">
      <LiteratureSearchResultItem
        v-for="doc in proximityDocs"
        :key="doc.id"
        :doc="doc"
        :highlights="searchResult?.highlighting?.[doc.id]?.bodytext"
        @show-search-result="highlights => handleShowSearchResult(doc, highlights, true)"
      />
    </template>
  </div>
</template>
